import React from 'react';
import { Box, Typography } from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { motion } from 'framer-motion';

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: 'small' | 'medium' | 'large';
  animated?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 'medium',
  animated = true
}) => {
  const starSize = size === 'small' ? 20 : size === 'medium' ? 30 : 40;

  // Special handling for Zero Stars (Critical/Urgent)
  if (rating === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          padding: '4px 8px',
          backgroundColor: '#000000',
          borderRadius: '4px',
          border: '2px solid #ff0000'
        }}>
          {[...Array(maxRating)].map((_, i) => (
            <StarBorder
              key={i}
              sx={{
                fontSize: starSize,
                color: '#ff0000'
              }}
            />
          ))}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: '#ff0000',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
        >
          URGENT
        </Typography>
      </Box>
    );
  }

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= maxRating; i++) {
      const isFilled = i <= rating;
      const StarComponent = motion(isFilled ? Star : StarBorder);

      stars.push(
        <StarComponent
          key={i}
          initial={animated ? { scale: 0 } : undefined}
          animate={animated ? { scale: 1 } : undefined}
          transition={animated ? { delay: i * 0.1, type: 'spring', stiffness: 260, damping: 20 } : undefined}
          sx={{
            fontSize: starSize,
            color: isFilled ? 'secondary.main' : 'action.disabled',
            filter: isFilled ? 'drop-shadow(0 0 4px rgba(245, 124, 0, 0.4))' : 'none'
          }}
        />
      );
    }
    return stars;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {renderStars()}
      <Typography variant="body2" sx={{ ml: 1, fontWeight: 'medium' }}>
        {rating}/{maxRating}
      </Typography>
    </Box>
  );
};

export default StarRating;